import { useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Lock, Loader2, Copy, Check, Film, MessageSquare, Sparkles, Target, TrendingUp, Users, Zap, Repeat, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ------------------------------------------------------------------ *
 *  DM auto-reply per la keyword "ORO"
 * ------------------------------------------------------------------ */
const ORO_DM =
  "Ciao! 👋 Grazie per aver scritto ORO.\n" +
  "Ti spiego in modo semplice come funziona l'oro fisico per proteggere i risparmi fermi — senza farti sentire stupido e senza costi nascosti.\n\n" +
  "Dimmi solo una cosa: parti da zero o hai già dato un'occhiata a come funziona? Così ti dico da dove conviene iniziare nel tuo caso. 🪙";

/* ------------------------------------------------------------------ *
 *  I 5 pattern del framework + le 5 componenti di retention
 * ------------------------------------------------------------------ */
const FIVE_PATTERNS = [
  { k: "95% non sa", d: "Sveli qualcosa che la maggioranza ignora" },
  { k: "Numeri tangibili", d: "Un dato concreto che si vede e si tocca" },
  { k: "Parole da bar", d: "Zero paroloni: lo spieghi come a un amico" },
  { k: "Emozione", d: "Tocchi la paura/desiderio reale del cliente" },
  { k: "Chiusura che eleva", d: "Chiudi facendo sentire capace, non stupido" },
];

const RETENTION_STEPS = [
  { t: "0–2s", k: "Hook", d: "Problema loro + promessa. In 3s si capisce di cosa parli" },
  { t: "2–4s", k: "Open loop", d: "Prometti un numero/rivelazione per la fine" },
  { t: "ogni 2–3s", k: "Corpo a scalini", d: "Una frase = un taglio: il montaggio veloce alza la retention" },
  { t: "payoff", k: "Chiudi il loop", d: "Sveli quello che avevi promesso all'inizio" },
  { t: "finale", k: "Re-hook", d: "L'ultima frase rimanda alla prima → fa riguardare (loop)" },
];

/* ------------------------------------------------------------------ *
 *  8 gancio-reel conversion-first (framework 5 pattern)
 *  Ogni reel: hook 3s · open loop · corpo a scalini · payoff · re-hook
 *  Caption chiude SEMPRE con una domanda per stimolare i commenti.
 *  CTA: "Scrivi ORO in DM"
 * ------------------------------------------------------------------ */
const HASHTAGS =
  "#protezionepatrimoniale #inflazione #orofisico #risparmi #liquidità #imprenditore #liberoprofessionista #beneriifugio #educazionefinanziaria #alexgold";

const GANCIO_REELS = [
  {
    pattern: "95% non sa",
    hook: "Il 95% di chi tiene i soldi fermi in banca non sa questa cosa.",
    openLoop: "E alla fine ti dico il numero che quasi nessuno controlla.",
    corpo: [
      ["4–7s", "Ogni anno che i tuoi risparmi restano fermi, l'inflazione se ne prende un pezzo."],
      ["7–10s", "Non lo vedi sul conto: il numero resta identico."],
      ["10–13s", "Ma quello che ci compri diventa sempre meno."],
      ["13–16s", "E chi gestisce le riserve degli Stati lo sa benissimo."],
    ],
    payoff: "Le banche centrali hanno comprato oltre 1.000 tonnellate d'oro fisico, per il terzo anno di fila (World Gold Council). Loro si proteggono.",
    rehook: "Ecco la cosa che il 95% non sa: mentre loro proteggono, tu lasci fermo. Riguarda l'inizio.",
    cta: "Scrivi ORO in DM: ti spiego da dove partire.",
    caption: "I tuoi soldi fermi non calano di numero, calano di valore. È inflazione, non pessimismo.",
    domanda: "Tu i risparmi li tieni fermi in banca o li stai già proteggendo? Scrivimelo nei commenti 👇",
  },
  {
    pattern: "Numeri tangibili",
    hook: "10.000€ in banca oggi. Sai quanto valgono davvero tra 5 anni?",
    openLoop: "Il numero finale ti sorprende — te lo dico tra pochi secondi.",
    corpo: [
      ["4–7s", "Sul conto vedrai sempre 10.000€. Il numero non cambia."],
      ["7–10s", "Ma con l'inflazione media degli ultimi anni…"],
      ["10–13s", "…il potere d'acquisto scende, anno dopo anno."],
      ["13–16s", "In pratica: stessi soldi, ma ci compri meno."],
    ],
    payoff: "In 5 anni puoi perdere intorno al 15–17% di potere d'acquisto (dati ISTAT). Su 10.000€ sono circa 1.500–1.700€ evaporati, senza toccare nulla.",
    rehook: "Ecco perché '10.000€ fermi' non vogliono dire 10.000€. Riguarda l'inizio.",
    cta: "Scrivi ORO in DM: ti mostro come si calcola sul tuo caso.",
    caption: "Il conto ti mostra il numero, non il valore reale. E la differenza, in 5 anni, pesa.",
    domanda: "Quanto pensavi di perdere in 5 anni? Scrivi la tua stima nei commenti 👇",
  },
  {
    pattern: "Parole da bar",
    hook: "Te lo spiego come al bar, senza paroloni: i soldi fermi sono ghiaccio nel bicchiere.",
    openLoop: "Alla fine capisci una cosa che quasi nessuno ti dice così semplice.",
    corpo: [
      ["4–7s", "Sembrano lì, interi, dentro il bicchiere."],
      ["7–10s", "Ma piano piano si sciolgono. Quello è l'inflazione."],
      ["10–13s", "Nessuno te lo dice perché sul conto il numero non cambia."],
      ["13–16s", "E intanto il bicchiere si svuota da solo."],
    ],
    payoff: "Un bene reale che non si scioglie esiste da 5.000 anni: l'oro fisico. Non è magia, è solo qualcosa che nessuno può stampare.",
    rehook: "Ecco perché i soldi fermi sono ghiaccio nel bicchiere. Riguarda l'inizio.",
    cta: "Scrivi ORO in DM: ti spiego da dove partire, senza farti sentire stupido.",
    caption: "L'inflazione è il ghiaccio che si scioglie: non la vedi sul conto, ma lavora ogni giorno.",
    domanda: "Ti hanno mai spiegato l'inflazione così semplice? Fammi sapere nei commenti 👇",
  },
  {
    pattern: "Emozione",
    hook: "Un cliente ha lavorato 30 anni per costruire qualcosa. Aveva una sola paura.",
    openLoop: "Una paura che forse hai anche tu — te la dico tra poco.",
    corpo: [
      ["4–7s", "Non voleva diventare ricco."],
      ["7–10s", "Voleva solo non perdere quello che aveva messo da parte."],
      ["10–13s", "Ma aveva liquidità ferma e non sapeva da dove partire."],
      ["13–16s", "E aveva il terrore di fare la scelta sbagliata e sentirsi stupido."],
    ],
    payoff: "Non gli ho promesso guadagni. Gli ho spiegato come mettere al riparo una parte con l'oro fisico. Oggi è più tranquillo perché capisce cosa fa.",
    rehook: "Quella paura di 'perdere ciò che ho costruito'? La conosco bene. Riguarda l'inizio.",
    cta: "Scrivi ORO in DM: partiamo dalla tua situazione.",
    caption: "Proteggere non è avere paura. È non voler buttare 30 anni di lavoro per non aver capito in tempo.",
    domanda: "Ti riconosci in questa paura? Raccontamela nei commenti 👇",
  },
  {
    pattern: "Chiusura che eleva",
    hook: "Se hai dei risparmi fermi, questa è per te.",
    openLoop: "Alla fine ti do la domanda che cambia tutto.",
    corpo: [
      ["4–7s", "Proteggere non vuol dire avere paura."],
      ["7–10s", "Vuol dire aver capito come funziona."],
      ["10–13s", "Le persone più tranquille non sono quelle che hanno di più."],
      ["13–16s", "Sono quelle che sanno cosa stanno facendo con i loro soldi."],
    ],
    payoff: "L'oro fisico è uno degli strumenti più antichi per questo: non per arricchirti, ma per non restare esposto senza saperlo.",
    rehook: "La domanda che cambia tutto: 'i miei soldi fermi, chi li sta proteggendo?'. Riguarda l'inizio.",
    cta: "Scrivi ORO in DM: ti spiego da dove partire.",
    caption: "La tranquillità non è avere tanto. È sapere esattamente cosa stai facendo con quello che hai.",
    domanda: "Chi sta proteggendo i tuoi risparmi oggi? Scrivilo nei commenti 👇",
  },
  {
    pattern: "95% non sa",
    hook: "'Tengo i soldi in banca, così sono al sicuro.' È l'errore più comune.",
    openLoop: "Ti dico perché — e alla fine il dettaglio che pochi conoscono.",
    corpo: [
      ["4–7s", "In banca i tuoi soldi sono al sicuro dai ladri, non dall'inflazione."],
      ["7–10s", "Il conto ti protegge il numero, non il valore."],
      ["10–13s", "E tecnicamente non sei il proprietario: sei un creditore della banca."],
      ["13–16s", "Sembra un dettaglio, ma cambia tutto."],
    ],
    payoff: "Un bene fisico che possiedi davvero, fuori dal circuito bancario, funziona all'opposto: è tuo e basta. L'oro fisico è uno di questi.",
    rehook: "Ecco perché 'in banca sono al sicuro' è vero solo a metà. Riguarda l'inizio.",
    cta: "Scrivi ORO in DM: ti spiego la differenza in 2 minuti.",
    caption: "Sicuro dai ladri ≠ sicuro dall'inflazione. E la seconda ti costa in silenzio, ogni anno.",
    domanda: "Lo sapevi di essere un creditore della banca, non un proprietario? Dimmelo nei commenti 👇",
  },
  {
    pattern: "Numeri tangibili",
    hook: "Nel 2024 è successa una cosa che i telegiornali hanno raccontato poco.",
    openLoop: "Il numero finale dice tutto — te lo do tra pochi secondi.",
    corpo: [
      ["4–7s", "Mentre a molti dicevano di lasciare i soldi fermi…"],
      ["7–10s", "…chi gestisce le riserve degli Stati faceva il contrario."],
      ["10–13s", "Compravano un bene reale, a ritmo record."],
      ["13–16s", "Non per speculare. Per proteggersi."],
    ],
    payoff: "Le banche centrali hanno comprato oltre 1.000 tonnellate d'oro, terzo anno di fila (World Gold Council). Se lo fanno loro, una domanda dovresti fartela.",
    rehook: "Ecco la notizia passata sotto silenzio. Riguarda l'inizio.",
    cta: "Scrivi ORO in DM: ti spiego cosa significa per te.",
    caption: "Gli Stati accumulano oro a ritmo record. A noi dicono di stare fermi. Coincidenze?",
    domanda: "Perché secondo te loro comprano oro e a noi dicono di stare fermi? Commenta 👇",
  },
  {
    pattern: "Parole da bar",
    hook: "'L'oro è roba da ricchi.' Ti spiego perché è falso.",
    openLoop: "Alla fine ti dico da quanto si può partire davvero.",
    corpo: [
      ["4–7s", "Non serve comprare un lingotto enorme."],
      ["7–10s", "Esistono tagli piccoli e accessibili."],
      ["10–13s", "La differenza non è quanto parti."],
      ["13–16s", "È che parti capendo cosa stai facendo."],
    ],
    payoff: "Si può iniziare anche con una piccola parte della liquidità ferma. L'importante è capire prima, decidere poi.",
    rehook: "Ecco perché 'l'oro è roba da ricchi' è solo una scusa. Riguarda l'inizio.",
    cta: "Scrivi ORO in DM: ti spiego da dove partire, anche con poco.",
    caption: "Il problema non sono le cifre. È che nessuno te lo spiega in modo semplice.",
    domanda: "Pensavi anche tu che servissero grandi cifre? Dimmelo nei commenti 👇",
  },
];

function buildScript(r, i) {
  const corpo = r.corpo.map(([t, line]) => `[${t}] ${line}`).join("\n");
  return (
`REEL ${i + 1} · Pattern: ${r.pattern}

🎬 HOOK (0–2s)
${r.hook}

🔁 OPEN LOOP (2–4s)
${r.openLoop}

📶 CORPO A SCALINI (un taglio ogni 2–3s)
${corpo}

🎯 PAYOFF (chiude il loop aperto)
${r.payoff}

♻️ RE-HOOK FINALE (rimanda al primo secondo → loop)
${r.rehook}

📱 CTA ON-SCREEN
${r.cta}`
  );
}

function buildCaption(r) {
  return `${r.caption}\n\n${r.cta}\n\n${r.domanda}\n\n${HASHTAGS}`;
}

/* ------------------------------------------------------------------ *
 *  Script chiamate (framework a 6 livelli · compliant)
 * ------------------------------------------------------------------ */
const CALL_SCRIPTS = [
  {
    id: "appuntamento",
    title: "Script 1 · Chiamata per fissare l'appuntamento",
    subtitle: "Con chi ti ha scritto ORO / ha preso la guida. Obiettivo unico: fissare la call da 20 minuti. Non vendere al telefono, vendi l'appuntamento.",
    stages: [
      { tag: "Apertura + permesso", note: "10 secondi. Se dice 'no': «Nessun problema, ti richiamo oggi pomeriggio o domani mattina?» e fissi comunque un orario.", say: "«Ciao [Nome], sono Alex Gold — ci eravamo sentiti [su Instagram / hai scaricato la mia guida sull'oro fisico]. Ti rubo un minuto, è il momento giusto per parlare?»" },
      { tag: "Motivo della chiamata", say: "«Ti chiamo perché mi hai scritto [ORO / hai preso la guida] e volevo capire meglio la tua situazione, così ti dico subito se ha senso approfondire nel tuo caso — o se magari non fa per te. Zero pressione.»" },
      { tag: "Mini-qualifica", note: "Fai parlare lui. Segna la sua parola esatta: la riuserai.", say: "«Posso farti due domande veloci?\n– Al momento hai della liquidità ferma in banca da un po'?\n– Cosa ti preoccupa di più: l'inflazione che la erode, o il non sapere dove altro metterla?»" },
      { tag: "Ponte al valore", say: "«Ok, capito. Quello che faccio è aiutare persone come te a trasformare quella liquidità ferma in oro fisico — un bene reale, fuori dalle banche — sempre con operatori OAM autorizzati. Non ti prometto guadagni: ti aiuto a proteggere quello che hai già costruito.»" },
      { tag: "Proposta appuntamento", note: "Dai sempre 2 orari, mai 'quando vuoi'.", say: "«La cosa più utile è una call di 20 minuti, senza impegno: ti spiego come funziona e capiamo insieme se ha senso per te. Ti va meglio giovedì alle 18 o venerdì mattina alle 11?»" },
      { tag: "Gestione obiezioni per fissare", sub: [
        { label: "«Mandami prima delle info»", say: "«Certo, te le mando. Però in 20 minuti al telefono ti chiarisco molto di più: capiamo il tuo caso, non l'oro in generale. Ti blocco giovedì alle 18, se poi non ti convince disdici con un messaggio. Ci sta?»" },
        { label: "«Non ho tempo»", say: "«Proprio per questo bastano 20 minuti, decidi tu se e come proseguire. Meglio in pausa pranzo o dopo cena?»" },
        { label: "«Ci devo pensare»", say: "«Giustissimo, non è una decisione da prendere di corsa. La call serve appunto a pensarci con le idee chiare, non a impegnarti a nulla. Fissiamo e vediamo?»" },
      ] },
      { tag: "Conferma e chiusura", say: "«Perfetto, allora [giorno] alle [ora]. Ti mando ora la conferma e un promemoria il giorno prima. Ti chiedo solo di essere in un posto tranquillo per 20 minuti. A [giorno], [Nome]!»" },
    ],
  },
  {
    id: "collaborazione",
    title: "Script 2 · Chiamata per la collaborazione",
    subtitle: "Con chi vuole collaborare: partner che ti segnalano clienti (Ramo A) o collaboratori attivi (Ramo B). Obiettivo: capire il fit e fissare un incontro di allineamento.",
    stages: [
      { tag: "Apertura + contesto", say: "«Ciao [Nome], sono Alex Gold. Ci siamo [conosciuti su Instagram / scritti / ti ha parlato di me X]. Ti chiamo perché sto costruendo una rete di collaborazioni selezionate e volevo capire con te se ha senso lavorare insieme. Hai due minuti?»" },
      { tag: "Focus su di lui", note: "Capisci se ha accesso al tuo pubblico o se vuole collaborare come collaboratore attivo.", say: "«Prima di dirti cosa faccio io: tu di cosa ti occupi oggi, e con che tipo di clienti/persone lavori di più?»" },
      { tag: "Cosa faccio io", say: "«Io aiuto imprenditori e professionisti a proteggere la liquidità ferma con l'oro fisico, sempre appoggiandomi a operatori OAM autorizzati. Niente promesse di rendimento: il mio lavoro è rendere semplice una cosa percepita complessa, senza far sentire nessuno stupido e senza costi nascosti.»" },
      { tag: "La proposta di collaborazione", note: "Scegli il ramo in base alla risposta del punto 2.", sub: [
        { label: "Ramo A — Partner che segnala clienti (commercialista, consulente)", say: "«Tu hai clienti che si fidano di te e che spesso hanno liquidità ferma senza sapere cosa farne. Io non tocco il tuo rapporto: mi limito a offrire loro un percorso chiaro e trasparente sull'oro fisico. Per te è un servizio in più da dare ai tuoi clienti, riconosciuto in modo trasparente. Ti interessa capire come funziona nel concreto?»" },
        { label: "Ramo B — Collaboratore attivo", say: "«Cerco persone serie che vogliano imparare questo lavoro e portarlo avanti con la mia stessa etica: trasparenza totale, zero hype, zero promesse. Ti do formazione, materiali e metodo. In cambio serve costanza e voglia di fare le cose fatte bene. È qualcosa che ti risuona?»" },
      ] },
      { tag: "Qualifica del fit", note: "Selezioni anche tu: se parla solo di 'soldi facili/veloci' non è il partner giusto.", say: "«Ti faccio una domanda diretta: perché vorresti collaborare proprio su questo tema? Cosa ti attira?»\n«E quanto tempo/energia realisticamente potresti dedicarci?»" },
      { tag: "Obiezioni → occasioni", sub: [
        { label: "«Non conosco bene l'oro»", say: "«Meglio così: ti formo io da zero, senza scorciatoie. L'importante è l'etica, il resto te lo insegno.»" },
        { label: "«Come vengo riconosciuto?»", say: "«Tutto trasparente e messo nero su bianco prima di partire. Nessuna zona grigia: è la base su cui costruisco.»" },
        { label: "«Devo pensarci»", say: "«Perfetto, è una collaborazione seria, non una cosa da decidere in una telefonata. Fissiamo un incontro di 30 minuti per vedere le carte con calma, poi decidi liberamente.»" },
      ] },
      { tag: "Chiusura → prossimo passo", say: "«Facciamo così: ci vediamo/sentiamo 30 minuti [in call / di persona] così ti mostro esattamente come lavoro e capiamo se siamo allineati. Ti va meglio martedì alle 17 o mercoledì alle 10?»" },
    ],
  },
];

function buildCallScript(s) {
  const parts = [s.title.toUpperCase(), s.subtitle, ""];
  s.stages.forEach((st, i) => {
    parts.push(`${i + 1}. ${st.tag.toUpperCase()}`);
    if (st.note) parts.push(`(${st.note})`);
    if (st.say) parts.push(st.say);
    if (st.sub) st.sub.forEach((x) => parts.push(`\n• ${x.label}:\n${x.say}`));
    parts.push("");
  });
  return parts.join("\n").trim();
}

/* ------------------------------------------------------------------ *
 *  Helpers UI
 * ------------------------------------------------------------------ */
function copyToClipboard(text, onDone) {
  const done = () => { toast.success("Copiato!"); onDone && onDone(); };
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(done).catch(() => fallback());
  } else { fallback(); }
  function fallback() {
    const ta = document.createElement("textarea");
    ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.select();
    try { document.execCommand("copy"); done(); } catch { toast.error("Impossibile copiare"); }
    document.body.removeChild(ta);
  }
}

function CopyButton({ text, testid, label = "Copia" }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => copyToClipboard(text, () => { setCopied(true); setTimeout(() => setCopied(false), 1500); })}
      data-testid={testid}
      className="flex items-center gap-1.5 text-xs font-semibold text-neutral-900 bg-amber-400 hover:bg-amber-300 rounded-full px-3 py-1.5 transition-colors shrink-0">
      {copied ? <><Check size={13} /> Copiato</> : <><Copy size={13} /> {label}</>}
    </button>
  );
}

function LockScreen({ onUnlock }) {
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!pwd) return;
    setLoading(true); setError("");
    try {
      const { data } = await axios.post(`${API}/ig/unlock`, { password: pwd });
      localStorage.setItem("ig_token", data.token);
      onUnlock();
    } catch { setError("Password errata. Riprova."); setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 grid place-items-center px-4" data-testid="pc-lock-screen">
      <Toaster theme="dark" position="top-right" />
      <div className="w-full max-w-sm bg-neutral-900/70 border border-neutral-800 rounded-3xl p-8 text-center">
        <div className="inline-grid place-items-center w-16 h-16 rounded-2xl mb-5 bg-amber-400">
          <Lock size={26} className="text-neutral-900" />
        </div>
        <h1 className="font-bold text-xl">Piano Contenuti</h1>
        <p className="text-sm text-neutral-500 mt-1 mb-6">Area privata · inserisci la password</p>
        <form onSubmit={submit} className="space-y-3">
          <input type="password" value={pwd} autoFocus placeholder="Password"
            onChange={(e) => setPwd(e.target.value)} data-testid="pc-password-input"
            className="w-full bg-neutral-950 border border-neutral-700 focus:border-amber-400 rounded-xl py-3 px-4 text-center focus:outline-none transition-colors" />
          {error && <p className="text-sm text-rose-400" data-testid="pc-password-error">{error}</p>}
          <button type="submit" disabled={loading || !pwd} data-testid="pc-unlock-button"
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-amber-400 hover:bg-amber-300 text-neutral-900 font-semibold py-3 disabled:opacity-60 transition-colors">
            {loading ? <Loader2 size={17} className="animate-spin" /> : <Lock size={16} />} Accedi
          </button>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 *  Card singolo Reel
 * ------------------------------------------------------------------ */
function ReelCard({ r, i }) {
  const script = buildScript(r, i);
  const caption = buildCaption(r);
  return (
    <section className="bg-neutral-900/70 border border-neutral-800 rounded-2xl p-5" data-testid={`gancio-reel-${i}`}>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="grid place-items-center w-9 h-9 rounded-xl bg-amber-400 text-neutral-900 font-bold text-sm">{i + 1}</div>
          <div>
            <h3 className="font-bold text-base leading-none">Reel {i + 1}</h3>
            <span className="text-[11px] font-mono uppercase tracking-[0.15em] text-amber-500/80">{r.pattern}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <CopyButton text={script} testid={`gancio-reel-${i}-copy-script`} label="Copione" />
          <CopyButton text={caption} testid={`gancio-reel-${i}-copy-caption`} label="Caption" />
        </div>
      </div>

      <div className="space-y-3">
        <ReelStep color="#f59e0b" tag="0–2s · HOOK" text={r.hook} />
        <ReelStep color="#3b82f6" tag="2–4s · OPEN LOOP" text={r.openLoop} />

        <div className="bg-neutral-950 border border-neutral-800 rounded-xl p-4">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-neutral-500 mb-2.5">Corpo a scalini · un taglio ogni 2–3s</p>
          <div className="space-y-2">
            {r.corpo.map(([t, line], k) => (
              <div key={k} className="flex gap-3">
                <span className="text-[10px] font-mono text-amber-500/70 shrink-0 w-14 pt-0.5">{t}</span>
                <span className="text-sm text-neutral-200 leading-snug">{line}</span>
              </div>
            ))}
          </div>
        </div>

        <ReelStep color="#22c55e" tag="PAYOFF · chiude il loop" text={r.payoff} />
        <ReelStep color="#a855f7" tag="RE-HOOK FINALE · rimanda all'inizio" text={r.rehook} />
        <ReelStep color="#f59e0b" tag="CTA ON-SCREEN" text={r.cta} />

        <div className="bg-neutral-950 border border-amber-800/40 rounded-xl p-4">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-amber-500/80 mb-2">Caption + domanda per i commenti</p>
          <p className="text-sm text-neutral-200 whitespace-pre-line leading-relaxed">{caption}</p>
        </div>
      </div>
    </section>
  );
}

function ReelStep({ color, tag, text }) {
  return (
    <div className="bg-neutral-950 border border-neutral-800 rounded-xl p-4 border-l-[3px]" style={{ borderLeftColor: color }}>
      <p className="text-[10px] font-mono uppercase tracking-[0.18em] mb-1.5" style={{ color }}>{tag}</p>
      <p className="text-sm text-neutral-100 leading-snug">{text}</p>
    </div>
  );
}

function CallScriptCard({ s, idx }) {
  const full = buildCallScript(s);
  return (
    <section className="bg-neutral-900/70 border border-neutral-800 rounded-2xl p-5" data-testid={`call-script-${idx}`}>
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-start gap-2.5">
          <div className="grid place-items-center w-9 h-9 rounded-xl bg-amber-400 text-neutral-900 shrink-0"><Phone size={17} /></div>
          <div>
            <h3 className="font-bold text-base leading-tight">{s.title}</h3>
            <p className="text-xs text-neutral-500 mt-1 max-w-xl leading-snug">{s.subtitle}</p>
          </div>
        </div>
        <CopyButton text={full} testid={`call-script-${idx}-copy`} label="Copia script" />
      </div>
      <div className="space-y-3">
        {s.stages.map((st, i) => (
          <div key={i} className="bg-neutral-950 border border-neutral-800 rounded-xl p-4" data-testid={`call-script-${idx}-stage-${i}`}>
            <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-amber-500/80 mb-2">{i + 1} · {st.tag}</p>
            {st.note && <p className="text-xs text-neutral-500 italic mb-2 leading-snug">{st.note}</p>}
            {st.say && <p className="text-sm text-neutral-100 whitespace-pre-line leading-relaxed">{st.say}</p>}
            {st.sub && (
              <div className="space-y-2.5 mt-2">
                {st.sub.map((x, k) => (
                  <div key={k} className="border-l-[3px] border-amber-700/50 pl-3">
                    <p className="text-xs font-semibold text-amber-200 mb-1">{x.label}</p>
                    <p className="text-sm text-neutral-200 whitespace-pre-line leading-relaxed">{x.say}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 *  Pagina
 * ------------------------------------------------------------------ */
export default function PianoContenuti() {
  const [unlocked, setUnlocked] = useState(() => !!localStorage.getItem("ig_token"));

  if (!unlocked) return <LockScreen onUnlock={() => setUnlocked(true)} />;

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100" data-testid="piano-contenuti-page">
      <Toaster theme="dark" position="top-right" />
      <header className="sticky top-0 z-20 border-b border-neutral-800 bg-neutral-950/90 backdrop-blur px-4 md:px-8 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div className="p-2 rounded-xl bg-amber-400">
            <Film size={20} className="text-neutral-900" />
          </div>
          <div>
            <h1 className="font-bold text-lg leading-none">Gancio-Reel · Conversione</h1>
            <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">Framework 5 pattern · pronti da girare</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-8 py-8 space-y-6">

        {/* Strategia · il collo di bottiglia */}
        <section className="bg-gradient-to-br from-amber-500/10 to-neutral-900/70 border border-amber-700/40 rounded-2xl p-5" data-testid="strategy-card">
          <div className="flex items-center gap-2 mb-3">
            <Target size={18} className="text-amber-400" />
            <h2 className="font-bold text-lg">Il collo di bottiglia è la conversione, non la reach</h2>
          </div>
          <p className="text-sm text-neutral-300 leading-relaxed">
            La reach funziona (59% non-follower). Il problema è a valle: chi arriva sul profilo non trova un motivo forte per agire.
            I contenuti lifestyle portano occhi, ma i <strong className="text-amber-300">gancio-reel del framework</strong> sono gli unici che generano follow reali.
            Da qui in poi la cadenza principale sono questi.
          </p>
          <div className="grid grid-cols-3 gap-3 mt-4">
            <Stat icon={Users} label="Non-follower" value="59%" note="la reach c'è" />
            <Stat icon={TrendingUp} label="Engagement" value="0,78%" note="da alzare con i commenti" />
            <Stat icon={Zap} label="Regola" value="3 sec" note="capire l'offerta subito" />
          </div>
        </section>

        {/* Struttura di retention */}
        <section className="bg-neutral-900/70 border border-neutral-800 rounded-2xl p-5" data-testid="retention-card">
          <div className="flex items-center gap-2 mb-4">
            <Repeat size={18} className="text-amber-400" />
            <h2 className="font-bold text-lg">Struttura di retention · presente in ogni reel</h2>
          </div>
          <div className="space-y-2.5">
            {RETENTION_STEPS.map((s, i) => (
              <div key={i} className="flex gap-3 items-baseline" data-testid={`retention-step-${i}`}>
                <span className="text-[10px] font-mono text-amber-500/70 shrink-0 w-16">{s.t}</span>
                <span className="text-sm text-neutral-100 font-semibold shrink-0 w-28">{s.k}</span>
                <span className="text-sm text-neutral-400 leading-snug">{s.d}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-neutral-800">
            <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-neutral-500 mb-2.5">I 5 pattern usati negli hook</p>
            <div className="flex flex-wrap gap-2">
              {FIVE_PATTERNS.map((p, i) => (
                <span key={i} title={p.d} data-testid={`pattern-chip-${i}`}
                  className="text-xs font-medium text-amber-200 bg-amber-500/10 border border-amber-700/40 rounded-full px-3 py-1">
                  {p.k}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* DM auto-reply ORO */}
        <section className="bg-neutral-900/70 border border-amber-800/40 rounded-2xl p-5" data-testid="dm-oro-section">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <MessageSquare size={18} className="text-amber-400" />
              <h2 className="font-bold text-lg">Risposta automatica DM · keyword "ORO"</h2>
            </div>
            <CopyButton text={ORO_DM} testid="dm-oro-copy" />
          </div>
          <p className="text-sm text-neutral-200 whitespace-pre-line leading-relaxed bg-neutral-950 border border-neutral-800 rounded-xl p-4">{ORO_DM}</p>
          <p className="text-[11px] text-neutral-500 mt-3">
            Imposta questa risposta automatica su chi scrive <strong className="text-amber-400">ORO</strong> in DM. È qui che avviene la vera conversione — non nel tap sul link in bio.
          </p>
        </section>

        {/* Separatore */}
        <div className="flex items-center gap-3 pt-2" data-testid="reels-heading">
          <div className="h-px flex-1 bg-neutral-800" />
          <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-neutral-600">8 gancio-reel pronti da girare</span>
          <div className="h-px flex-1 bg-neutral-800" />
        </div>

        {/* Reels */}
        {GANCIO_REELS.map((r, i) => <ReelCard key={i} r={r} i={i} />)}

        {/* Script chiamate */}
        <div className="flex items-center gap-3 pt-2" data-testid="scripts-heading">
          <div className="h-px flex-1 bg-neutral-800" />
          <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-neutral-600">Script chiamate · pronti da usare</span>
          <div className="h-px flex-1 bg-neutral-800" />
        </div>
        {CALL_SCRIPTS.map((s, i) => <CallScriptCard key={s.id} s={s} idx={i} />)}

        <p className="text-center text-[11px] text-neutral-600 pt-2">
          Piano Contenuti Alex Gold · ogni reel chiude con una domanda (commenti) e CTA → Scrivi ORO in DM
        </p>
      </main>
    </div>
  );
}

function Stat({ icon: Icon, label, value, note }) {
  return (
    <div className="bg-neutral-950 border border-neutral-800 rounded-xl p-3 text-center">
      <Icon size={15} className="text-amber-400 mx-auto mb-1.5" />
      <p className="font-bold text-lg leading-none">{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-neutral-500 mt-1">{label}</p>
      <p className="text-[10px] text-neutral-600 mt-0.5">{note}</p>
    </div>
  );
}
